import React, { forwardRef } from 'react';
import { cn } from 'src/utils/className';
import { Label } from './Label';

export type InputProps = {
  label?: React.ReactNode;
  error?: string;
  variant?: 'outlined' | 'contained' | 'text';
  color?: 'primary' | 'secondary' | 'default';
  size?: 'small' | 'medium' | 'large';
  className?: string;
  labelClassName?: string;
  required?: boolean;
  disabled?: boolean;
  subText?: string;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  inputAction?: () => void;
  iconAction?: () => void;
  fullWidth?: boolean;
  inline?: boolean;
  allowHtml?: boolean;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'>;

const sizeClasses = {
  small: 'px-2 py-1 text-sm',
  medium: 'px-3 py-2 text-base',
  large: 'px-4 py-3 text-lg',
};

const variantClasses = {
  outlined: {
    base: 'border',
    primary: 'border-neutral-200 hover:border-primary focus:border-primary',
    secondary:
      'border-neutral-200 hover:border-neutral-400 focus:border-neutral-400',
    default: 'border-neutral-200',
  },
  contained: {
    base: 'border-0',
    primary: 'bg-neutral-50 hover:bg-neutral-100 focus:bg-neutral-100',
    secondary: 'bg-neutral-50 hover:bg-neutral-100 focus:bg-neutral-100',
    default: 'bg-neutral-50 hover:bg-neutral-100 focus:bg-neutral-100',
  },
  text: {
    base: 'border-0',
    primary: 'hover:bg-neutral-50 focus:bg-neutral-50',
    secondary: 'hover:bg-neutral-50 focus:bg-neutral-50',
    default: 'hover:bg-neutral-50 focus:bg-neutral-50',
  },
};

const sanitizeHtml = (value: string): string => {
  return value.replace(/<[^>]*>/g, '');
};

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      error,
      variant = 'outlined',
      color = 'primary',
      size = 'medium',
      className,
      labelClassName = '',
      required = false,
      disabled = false,
      subText,
      icon,
      iconPosition = 'right',
      inputAction,
      iconAction,
      fullWidth = true,
      inline = false,
      allowHtml = false,
      onChange,
      ...props
    },
    ref,
  ) => {
    const variantClass = variantClasses[variant];
    const sizeClass = sizeClasses[size];

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!allowHtml) {
        const sanitizedValue = sanitizeHtml(e.target.value);
        const sanitizedEvent = {
          ...e,
          target: {
            ...e.target,
            value: sanitizedValue,
          },
        };
        onChange?.(sanitizedEvent as React.ChangeEvent<HTMLInputElement>);
      } else {
        onChange?.(e);
      }
    };

    return (
      <div
        className={cn(fullWidth ? 'w-full' : '', inline ? 'inline-block' : '')}
      >
        {label && (
          <Label
            htmlFor={props.id}
            className={cn(
              error ? 'text-error' : 'text-neutral-600',
              labelClassName,
            )}
            required={required}
          >
            {label}
          </Label>
        )}

        <div className="relative">
          {icon && iconPosition === 'left' && (
            <button
              onClick={iconAction || inputAction}
              type="button"
              className={cn(
                'absolute inset-y-0 flex items-center text-neutral-400',
                'left-0 pl-3',
                disabled && 'cursor-not-allowed text-neutral-400',
              )}
              disabled={disabled}
            >
              {icon}
            </button>
          )}
          <input
            ref={ref}
            {...props}
            onChange={handleChange}
            disabled={disabled}
            className={cn(
              fullWidth && 'w-full',
              'rounded-md transition-colors duration-200',
              'focus:outline-none',
              variantClass.base,
              error ? 'error' : variantClass[color],
              error ? 'focus:ring-error/20' : 'focus:ring-primary/20',
              sizeClass,
              disabled && 'cursor-not-allowed bg-neutral-50 text-neutral-400',
              'leading-[1.7] placeholder:text-[14.9px]',
              className,
              icon && iconPosition === 'left' && 'pl-10',
              icon && iconPosition === 'right' && 'pr-10',
            )}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && inputAction) {
                inputAction();
              }
            }}
          />
          {icon && iconPosition === 'right' && (
            <button
              onClick={iconAction || inputAction}
              type="button"
              className={cn(
                'absolute inset-y-0 flex items-center text-neutral-400',
                'right-0 pr-3',
                disabled && 'cursor-not-allowed text-neutral-400',
              )}
              disabled={disabled}
            >
              {icon}
            </button>
          )}
        </div>

        {(error || subText) && (
          <p
            className={cn(
              'mt-1 text-sm',
              error ? 'text-error' : 'text-neutral-400',
            )}
          >
            {error || subText}
          </p>
        )}
      </div>
    );
  },
);

Input.displayName = 'Input';
